import {
    bexHttpClient
} from 'shared/api/clients';
import {
    UploadFilesRequest,
    UploadFilesResponse,
    GeneratePresignedUrlsRequest,
    GeneratePresignedUrlsResponse,
    GetPresignedGetUrlsRequest,
    GetPresignedGetUrlsResponse,
    DeleteFilesRequest,
    DeleteFilesResponse,
    ListFilesResponse,
    ListFilesWithPresignedUrlResponse
} from '../bexService.types';

/**
 * Service class for performing File Manager operations.
 */
class FileManagerService {

    /**
     * Upload files to COS.
     * @returns {Promise<Object>}
     */
    async uploadFiles(data: UploadFilesRequest): Promise<UploadFilesResponse> {
        const response = await bexHttpClient.post('/cloud-storage/upload-files', data, {
            headers: {
                accept: 'application/json',
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    }

    /**
     * get pre-signed urls from COS. (Upload)
     * @param {PresignedURLsRequest} data The payload to be sent
     * @returns {Promise<Object>}
     * Example data:
     * ```
     * {
     *  organizationId: "ORG_ID",
     *  bucketId: "BUCKET_ID",
     *  prefix: "FOLDER1/SUBFOLDER",
     *  fileNames: ["file1.pdf", "file2.pdf"],
     *  metadataArray : []
     * }
     * ```
     */
    async getPreSignedUrls(data: GeneratePresignedUrlsRequest): Promise<GeneratePresignedUrlsResponse> {
        const response = await bexHttpClient.post('/cloud-storage/generate-presigned-urls', data);
        return response;
    }

    /**
     * get public pre-signed urls from COS.
     * @param {PresignedURLsRequest} data The payload to be sent
     * @returns {Promise<Object>}
     * Example data:
     * ```
     * {
     *  organizationId: "ORG_ID",
     *  bucketId: "BUCKET_ID",
     *  prefix: "FOLDER1/SUBFOLDER",
     *  fileNames: ["file1.pdf", "file2.pdf"],
     *  metadataArray : []
     * }
     * ```
     */
    async getPublicUrls(data: GetPresignedGetUrlsRequest): Promise<GetPresignedGetUrlsResponse> {
        const response = await bexHttpClient.post('/cloud-storage/get-presigned-urls', data);
        return response;
    }

    /**
     * delete files in cos, data include organizationId, bucketId, array of files keys
     * @returns {Promise<any[]>}
     */
    async deleteFiles(data: DeleteFilesRequest): Promise<DeleteFilesResponse> {
        const response = await bexHttpClient.delete('/cloud-storage/delete-files', {
            data
        });
        return response;
    }

    /**
     * delete files in cos, data include organizationId, bucketId, array of files keys
     * @returns {Promise<any[]>}
     */
    async listFiles(organizationId: string, bucketId: string, prefix?: string | null): Promise<ListFilesResponse> {
        let url = `/cloud-storage/list-files?organizationId=${organizationId}&bucketId=${bucketId}`;
        if (prefix) {
            url += `&prefix=${prefix}`;
        }
        const response = await bexHttpClient.get(url);
        return response;
    }

    /**
     *
     * @returns {Promise<any[]>}
     */

    //list all of the files in the bucket with presigned url
    async listFilesWithPresignedUrl(
        organizationId: string,
        bucketId: string,
        prefix?: string | null,
        paginated = false,
        continuationToken: string | null = null,
        delimiter: string | null = null
    ): Promise<ListFilesWithPresignedUrlResponse> {
        let url = `/cloud-storage/list-files-with-presigned-url?organizationId=${organizationId}&bucketId=${bucketId}`;
        if (prefix) {
            url += `&prefix=${prefix}`;
        }
        if (paginated) {
            url += '&isPagination=true';
        }
        if (continuationToken) {
            url += `&continuationToken=${continuationToken}`;
        }
        if (delimiter) {
            url += `&delimiter=${delimiter}`;
        }
        const response = await bexHttpClient.get(url);
        return response;
    }

}

const fileManagerService = new FileManagerService();
export { fileManagerService };
